'use client';

import { Component, type ReactNode } from 'react';

/**
 * Keeps a failing location widget inside its own section.
 *
 * The widget's cards are client components, so React renders them into the page's HTML on the
 * server. An error there is not catchable by the server component above it — the children
 * render later in the same stream — and React responds by discarding the whole document and
 * handing the route's error boundary an empty page. One broken label in one card therefore
 * replaced an entire activity with "something went wrong".
 *
 * A class component because this is the one thing hooks cannot express, and it runs during
 * server rendering as well as in the browser, which is the case that matters here.
 */
export class LocationWidgetBoundary extends Component<
    { fallback: ReactNode; children: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: unknown) {
        // Named so it is findable in the runtime logs: a section that silently degrades is
        // indistinguishable from a group that has no partners to show.
        console.error('[location-widget] render failed, showing regions only', error);
    }

    render() {
        return this.state.hasError ? this.props.fallback : this.props.children;
    }
}
